/**
 * Module Studio (UPT-2079 lot L2, §4.3) — diff unifié de deux contenus, au
 * FORMAT EXACT du serveur (rest §4.5) : lignes = contenu normalisé privé d'un LF
 * final puis découpé sur LF ; algorithme de Myers (O(ND)) sur les lignes,
 * comparaison exacte ; sortie `--- v{from}` / `+++ v{to}` puis des blocs
 * `@@ -a,b +c,d @@` (b et d toujours écrits, a et c = 0 quand le côté est
 * vide), lignes préfixées « ␠ », « - », « + » ; `context` lignes de contexte ;
 * deux changements dont les contextes se recouvrent forment un seul bloc ;
 * contenus identiques → les deux lignes d'en-tête seulement.
 *
 * Fonction pure, sans dépendance : le dépôt de démonstration et la vue
 * « Comparer mon brouillon » l'utilisent ; les vecteurs de rest §7.3 sont
 * repris tels quels dans `unifiedDiff.test.ts`.
 */
import { DESIGN_LIMITS } from '../data/contracts';
import { contentLines } from './specContent';

export interface UnifiedDiffResult {
  unified: string;
  added: number;
  removed: number;
}

type EditOp =
  | { kind: 'equal'; oldIndex: number; newIndex: number }
  | { kind: 'delete'; oldIndex: number }
  | { kind: 'insert'; newIndex: number };

/** Script d'édition minimal (Myers, « An O(ND) Difference Algorithm », avec trace pour la remontée). */
const myersEditScript = (a: string[], b: string[]): EditOp[] => {
  const n = a.length;
  const m = b.length;
  const max = n + m;
  if (max === 0) {
    return [];
  }
  const offset = max;
  const v = new Array<number>(2 * max + 2).fill(0);
  const trace: number[][] = [];
  let found = false;
  for (let d = 0; d <= max && !found; d += 1) {
    trace.push(v.slice());
    for (let k = -d; k <= d; k += 2) {
      let x: number;
      if (k === -d || (k !== d && v[offset + k - 1] < v[offset + k + 1])) {
        x = v[offset + k + 1];
      } else {
        x = v[offset + k - 1] + 1;
      }
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) {
        x += 1;
        y += 1;
      }
      v[offset + k] = x;
      if (x >= n && y >= m) {
        found = true;
        break;
      }
    }
  }

  const ops: EditOp[] = [];
  let x = n;
  let y = m;
  for (let d = trace.length - 1; d >= 0; d -= 1) {
    const snapshot = trace[d];
    const k = x - y;
    let prevK: number;
    if (k === -d || (k !== d && snapshot[offset + k - 1] < snapshot[offset + k + 1])) {
      prevK = k + 1;
    } else {
      prevK = k - 1;
    }
    const prevX = snapshot[offset + prevK];
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      ops.push({ kind: 'equal', oldIndex: x - 1, newIndex: y - 1 });
      x -= 1;
      y -= 1;
    }
    if (d > 0) {
      if (x === prevX) {
        ops.push({ kind: 'insert', newIndex: y - 1 });
      } else {
        ops.push({ kind: 'delete', oldIndex: x - 1 });
      }
    }
    x = prevX;
    y = prevY;
  }
  ops.reverse();
  return ops;
};

interface Hunk {
  /** Indices (dans `ops`) du premier et du dernier élément du bloc, inclus. */
  start: number;
  end: number;
}

/**
 * Diff unifié entre deux contenus (texte brut, normalisé ici) ; `fromVersion` et
 * `toVersion` ne servent qu'aux deux lignes d'en-tête (`from` = 0 désigne le vide).
 */
export function unifiedDiff(
  fromText: string,
  toText: string,
  fromVersion: number,
  toVersion: number,
  context: number = DESIGN_LIMITS.diffContextLines
): UnifiedDiffResult {
  const a = contentLines(fromText);
  const b = contentLines(toText);
  const header = `--- v${fromVersion}\n+++ v${toVersion}`;
  const ops = myersEditScript(a, b);

  const changeIndexes = ops
    .map((op, index) => (op.kind === 'equal' ? -1 : index))
    .filter((index) => index >= 0);
  if (changeIndexes.length === 0) {
    return { unified: header, added: 0, removed: 0 };
  }

  // Blocs : chaque changement s'entoure de `context` lignes ; un changement dont la
  // fenêtre recouvre celle du bloc courant le prolonge (rest §4.5, vecteurs §7.3).
  const hunks: Hunk[] = [];
  for (const index of changeIndexes) {
    const start = Math.max(0, index - context);
    const end = Math.min(ops.length - 1, index + context);
    const current = hunks[hunks.length - 1];
    if (current && start <= current.end) {
      current.end = Math.max(current.end, end);
    } else {
      hunks.push({ start, end });
    }
  }

  let added = 0;
  let removed = 0;
  const blocks: string[] = [];
  for (const hunk of hunks) {
    const slice = ops.slice(hunk.start, hunk.end + 1);
    const oldLines = slice.filter((op): op is Exclude<EditOp, { kind: 'insert' }> => op.kind !== 'insert');
    const newLines = slice.filter((op): op is Exclude<EditOp, { kind: 'delete' }> => op.kind !== 'delete');
    const oldStart = oldLines.length > 0 ? oldLines[0].oldIndex + 1 : 0;
    const newStart = newLines.length > 0 ? newLines[0].newIndex + 1 : 0;
    const lines: string[] = [`@@ -${oldStart},${oldLines.length} +${newStart},${newLines.length} @@`];
    // Dans un bloc, chaque suite de changements s'écrit retraits d'abord, ajouts ensuite.
    let pendingInserts: string[] = [];
    const flushInserts = () => {
      for (const line of pendingInserts) {
        lines.push(`+${line}`);
      }
      pendingInserts = [];
    };
    for (const op of slice) {
      if (op.kind === 'equal') {
        flushInserts();
        lines.push(` ${a[op.oldIndex]}`);
      } else if (op.kind === 'delete') {
        lines.push(`-${a[op.oldIndex]}`);
        removed += 1;
      } else {
        pendingInserts.push(b[op.newIndex]);
        added += 1;
      }
    }
    flushInserts();
    blocks.push(lines.join('\n'));
  }

  return { unified: `${header}\n${blocks.join('\n')}`, added, removed };
}
